import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { ProcessesApi } from "../api";
import { RydeProcessTitle } from "../enums";
import {
  getActiveProcessKey,
  Log,
  startPolling,
  stopAllPolling,
  stopPolling,
} from "../helpers";
import { RydeUiUnit } from "../models";
import { activeProcessesAtom, processAtom } from "../recoil";

interface IProcessPollingArguments {
  id: number;
  processTitle: RydeProcessTitle;
  entity: RydeUiUnit;
  // optional company override for global pages whose process is not the selected company
  companyId?: number;
}

export const useProcesses = () => {
  const setProcess = useSetRecoilState(processAtom);
  const setActiveProcess = useSetRecoilState(activeProcessesAtom);

  const startProcessPolling = useEvent(
    ({ processTitle, id, entity, companyId }: IProcessPollingArguments) => {
      startPolling({
        id,
        setProcess,
        fetchProcess: processId => ProcessesApi.getOne(processId, companyId),
      });
      setActiveProcess(prev => {
        const copy = new Map(prev);
        const key = getActiveProcessKey(processTitle, entity);

        if (copy.has(key)) {
          stopPolling(id);
          Log.warn(
            `Active process with key ${key} already exists. Overwriting it with id ${id}`,
          );
        }

        copy.set(key, id);
        return copy;
      });
    },
  );

  const stopProcessPolling = useEvent(
    ({ id, entity, processTitle }: IProcessPollingArguments) => {
      stopPolling(id);
      setActiveProcess(prev => {
        const key = getActiveProcessKey(processTitle, entity);

        if (!prev.has(key)) {
          return prev;
        }

        const copy = new Map(prev);
        copy.delete(key);
        return copy;
      });
    },
  );

  const stopAllProcessPolling = useEvent(() => {
    stopAllPolling();
    setActiveProcess(new Map());
  });

  return { startProcessPolling, stopProcessPolling, stopAllProcessPolling };
};
